/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext } from 'react';
import { Divider, Row, Col, Tabs, Skeleton, Popover } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import IconHistory from 'shared/assets/icons/history.svg';
import { useQuery } from '@apollo/react-hooks';
import formatCurrency from 'shared/utils/formatCurrency';
import { formatDate } from 'shared/utils/dateTime';
import { color } from 'shared/utils/styles';
import { Text, FileList, Button } from 'shared/components';
import s3 from 'shared/config/s3';
import { v4 as uuidv4 } from 'uuid';
import { useFormContext } from 'react-hook-form';
import RECEIPT_QUERY, { ReceiptData, ReceiptVars } from 'shared/graphql/query/receipt';
import EMAIL_LOGS_QUERY, {
  EmailLogs,
  EmailLogsData,
  EmailLogsVars,
} from 'shared/graphql/query/emailLogs';
import { ReceiptContext } from 'views/AccountReceive/common/context/ReceiptContext';
import { ReceiptStatus } from 'views/AccountReceive/common/model/receiptStatus.model';
import { TagStatusReceipt } from 'views/AccountReceive/common/components';
import SelectFieldSearchCustomers from './SelectFieldSearchCustomers';
import InvoiceTable from './InvoiceTable';
import { StyledHistoryPopover } from './Styles';

const { TabPane } = Tabs;

const ReceiptDetail: React.FC = () => {
  const { selectedReceiptDetail } = useContext(ReceiptContext);
  const { receiptId } = selectedReceiptDetail;
  const { getValues, setValue, watch } = useFormContext();
  const watchFields = watch(['payerId', 'payerFullName']);
  const { data } = useQuery<ReceiptData, ReceiptVars>(RECEIPT_QUERY, {
    variables: {
      _id: receiptId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: emailLogsData } = useQuery<EmailLogsData, EmailLogsVars>(EMAIL_LOGS_QUERY, {
    variables: {
      receipt: receiptId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const contentHistory = (value: EmailLogs[]) => {
    return value.map((item, index) => {
      return (
        <div key={uuidv4()}>
          <Row gutter={[0, 0]} justify="space-between">
            <Col span={24}>
              <Text strong>ครั้งที่ {index + 1}: </Text>
              <Text>{formatDate(item.create_date)} </Text>
              <Text>{item.status_code === '202' ? 'ส่งสำเร็จ' : 'ส่งไม่สำเร็จ'} </Text>
            </Col>
          </Row>
        </div>
      );
    });
  };

  let content = <Skeleton />;

  if (data?.receipt) {
    const {
      receipt_number,
      receipt_status,
      payer,
      channel,
      bank_name,
      account_number,
      receipt_date,
      total_receipt,
      invoice,
      billing_note,
      proof_of_payment,
      cancel_remark,
    } = data.receipt;

    content = (
      <>
        <Text strong>เงื่อนไขใบเสร็จ</Text>
        <Divider style={{ margin: '10px 0' }} />
        <Row gutter={[32, 16]}>
          <Col span={8} style={{ borderRight: `1px dashed ${color.grey}` }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Row justify="space-between">
                  <Col>
                    ลูกค้า <Text customType="grey">(ลูกหนี้)</Text>:
                  </Col>
                  <Col span={24}>
                    {watchFields && (
                      <SelectFieldSearchCustomers
                        value={{
                          key: getValues('payerId'),
                          label: getValues('payerFullName'),
                        }}
                        onChange={(value) => {
                          setValue('payerId', value.value);
                          setValue('payerFullName', value.label);
                        }}
                        disabled
                      />
                    )}
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row>
                  <Col span={8}>ช่องทางการรับ:</Col>
                  <Col span={16}>
                    <Text strong>{channel || '-'}</Text>
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>ธนาคารที่รับชำระ:</Col>
                  <Col span={16}>
                    <Text strong>{bank_name || '-'}</Text>
                  </Col>
                </Row>
                <Row>
                  <Col span={8}>เลขบัญชี:</Col>
                  <Col span={16}>
                    <Text strong>{account_number || '-'}</Text>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col span={8} style={{ borderRight: `1px dashed ${color.border}` }}>
            <Row>
              <Col span={10}>ประเภทงาน:</Col>
              <Col span={14}>
                <Text strong>{invoice.system_request || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>ที่อยู่วางบิล:</Col>
              <Col span={14}>
                <Text strong>{payer.address || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>เลขประจำตัวผู้เสียภาษี:</Col>
              <Col span={14}>
                <Text strong>{payer.tax_id || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>เบอร์โทรศัพท์:</Col>
              <Col span={14}>
                <Text strong>{payer.phone.length > 0 ? payer.phone.join(', ') : '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={10}>อีเมล:</Col>
              <Col span={14}>
                <Text strong>{payer.email.length > 0 ? payer.email.join(', ') : '-'}</Text>
              </Col>
            </Row>
          </Col>

          <Col span={8}>
            <Row>
              <Col span={8}>วันที่เอกสาร:</Col>
              <Col span={16}>
                <Text strong>{formatDate(receipt_date)}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={8}>รหัสใบเสร็จ:</Col>
              <Col span={16}>
                <Text strong>{receipt_number}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={8}>รวมมูลค่าใบแจ้งหนี้:</Col>
              <Col span={16}>
                <Text strong customType="primary">
                  {formatCurrency(total_receipt)}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col span={8}>สถานะ:</Col>
              <Col span={16}>
                <TagStatusReceipt status={receipt_status} />
              </Col>
            </Row>
            {cancel_remark && (
              <Row>
                <Col span={8}>เหตุผลยกเลิก:</Col>
                <Col span={16}>
                  <Text strong customType="danger">
                    {cancel_remark}
                  </Text>
                </Col>
              </Row>
            )}
            <Row>
              <Col span={8}>รหัสใบแจ้งหนี้:</Col>
              <Col span={16}>
                <Text strong>{invoice.invoice_number || '-'}</Text>
              </Col>
            </Row>
            <Row>
              <Col span={8}>รหัสใบวางบิล:</Col>
              <Col span={16}>
                <Text strong>{billing_note.bill_number || '-'}</Text>
              </Col>
            </Row>
            {receipt_status === ReceiptStatus.COMPLETE && (
              <Row>
                <Col span={8}>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    อีเมลลูกค้า:
                    {emailLogsData && emailLogsData.emailLogs[0] && emailLogsData?.emailLogs[0].to && (
                      <StyledHistoryPopover>
                        <Popover placement="top" content={contentHistory(emailLogsData.emailLogs)}>
                          <Button icon={<IconHistory />} size="small" shape="circle" />
                        </Popover>
                      </StyledHistoryPopover>
                    )}
                  </div>
                </Col>
                <Col span={16}>
                  <Row>
                    <Col>
                      <Text strong>
                        {/* <TextExpand expand={!emailLogsLoading}> */}
                        {emailLogsData &&
                        emailLogsData.emailLogs[0] &&
                        emailLogsData?.emailLogs[0].to
                          ? emailLogsData?.emailLogs[0].to.map((logs) => logs.email).join(', ')
                          : 'ยังไม่ได้ส่ง'}
                        {/* </TextExpand> */}
                      </Text>
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        <Divider style={{ margin: 0, marginTop: -5 }} />

        <Tabs defaultActiveKey="1" animated={false}>
          <TabPane
            tab={
              <span>
                รายละเอียดงาน{' '}
                <CheckCircleOutlined
                  style={{
                    color: color.success,
                    marginLeft: 5,
                  }}
                />
              </span>
            }
            key="1"
          >
            <InvoiceTable />
          </TabPane>
          <TabPane
            tab={
              <span>
                หลักฐานการชำระ{' '}
                <CheckCircleOutlined
                  style={{
                    color: color.success,
                    marginLeft: 5,
                  }}
                />
              </span>
            }
            key="2"
          >
            <div>
              <FileList files={proof_of_payment.map((item) => `${s3}/${item}`)} />
            </div>
          </TabPane>
        </Tabs>
      </>
    );
  }

  return content;
};

export default ReceiptDetail;
